import { Loader2 } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/lib/auth'

const API_BASE = import.meta.env.VITE_API_URL || ''

/**
 * OAuth callback page — fixed path /auth/callback.
 * Receives authorization code via query params (Authorization Code Flow + PKCE).
 * Exchanges code for self-issued JWT via backend token-exchange endpoint.
 * state parameter carries "{slug}:{redirectPath}" to restore context.
 */
export default function AuthCallbackPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const exchangeStarted = useRef(false)

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (exchangeStarted.current) return
    exchangeStarted.current = true

    const code = searchParams.get('code')
    const state = searchParams.get('state') ?? ''

    // Decode state: "{slug}:{redirectPath}"
    const separatorIdx = state.indexOf(':')
    const slug = separatorIdx > 0 ? state.substring(0, separatorIdx) : ''
    const redirectTo = separatorIdx > 0 ? state.substring(separatorIdx + 1) : ''
    const fallback = slug ? `/${slug}/dashboard` : '/'

    if (!code) {
      navigate(slug ? `/${slug}/login` : '/login', { replace: true })
      return
    }

    // Retrieve PKCE code_verifier from sessionStorage
    const codeVerifier = sessionStorage.getItem('pkce_code_verifier') || ''
    sessionStorage.removeItem('pkce_code_verifier')

    if (!codeVerifier) {
      console.error('PKCE code_verifier not found in sessionStorage')
      navigate(slug ? `/${slug}/login` : '/login', { replace: true })
      return
    }

    // Exchange code for JWT via backend
    fetch(`${API_BASE}/api/auth/token-exchange`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        code,
        codeVerifier,
        tenantSlug: slug,
        redirectUri: `${window.location.origin}/auth/callback`,
      }),
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('Token exchange failed')
        const body = await res.json()
        return login(body.data.token)
      })
      .then(() => navigate(redirectTo || fallback, { replace: true }))
      .catch(() => navigate(slug ? `/${slug}/login` : '/login', { replace: true }))
  }, [login, navigate, searchParams])

  return (
    <div className="flex min-h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
    </div>
  )
}
